import type { paths } from "@repo/api-types/merchant"
import type { OrderDetailVO } from "../user/types/member"
import type * as Order from "./types/order"
import type * as Product from "./types/product"
import type { SensitiveApprovalRequestConfig } from "@/utils/service"
import { request } from "@/utils/service"
import {
  buildOrderRequestFingerprint,
  clearOrderRequestId,
  getOrCreateOrderRequestId,
} from "./order-idempotency"
import { normalizeOrderDiscountRatesForBackend } from "./orderDiscountRate"

const PREFIX = "teashop_merchant/"

const shiftMutationRoutes = {
  reverse_close_order: "/merchant_oms_order/shift/{shiftHandoverId}/reverse_close_order",
  write_off: "/merchant_oms_order/shift/{shiftHandoverId}/write_off",
} satisfies Record<string, keyof paths>

function orderMutationUrl(action: keyof typeof shiftMutationRoutes, shiftId?: number) {
  if (shiftId === undefined) return `${PREFIX}merchant_oms_order/${action}`
  if (!Number.isSafeInteger(shiftId) || shiftId <= 0) throw new Error("班次标识无效，请重新打开账单")
  return `teashop_merchant${shiftMutationRoutes[action]}`.replace("{shiftHandoverId}", String(shiftId))
}

/** 获取订单详情 */
export function getOrderDetailInfo(orderId: number) {
  return request<IApiResponseData<OrderDetailVO>>({
    url: `${PREFIX}merchant_oms_order/get_order_detail/${orderId}`,
    method: "GET",
    customHeaders: {
      'unmask': '1',
    },
  })
}

/** 获取订单列表 */
export function getOrderList(orderStatus: number, current: number, size: number) {
  return request<IApiPageResponseData<Order.OrderItemType[]>>({
    url: `${PREFIX}merchant_oms_order/page`,
    method: "GET",
    params: {
      orderStatus,
      current,
      size,
    },
  })
}

/** 修改订单状态 */
export function updateOrderStatus(data: Order.OrderItemType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/update_order_status`,
    method: "POST",
    data,
  })
}

/** 修改订单支付状态 */
export function updateOrderPayStatus(data: Order.OrderItemType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/update_pay_status`,
    method: "POST",
    data,
  })
}

/** 修改订单优惠信息 */
export function updateOrderDiscount(data: Order.OrderItemType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/update_discount_info`,
    method: "POST",
    data,
  })
}

/** 检查订单折扣相关业务权限 */
export function checkOrderDiscountPermissions(data: Order.DiscountVO[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/check/order/discount`,
    method: "POST",
    data: normalizeOrderDiscountRatesForBackend(data),
  })
}

/** 修改订单关联会员 */
export function updateOrderMember(orderId: number, memberId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/update_member`,
    method: "POST",
    params: {
      orderId,
      memberId,
    },
  })
}

/** 按桌台记录同步活动结算订单与桌台关联会员 */
export function syncTableOrderMember(tableRecordId: number, memberId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/sync_table_member`,
    method: "POST",
    params: {
      tableRecordId,
      memberId,
    },
  })
}

/** 修改订单备注 */
export function updateOrderRemark(data: Order.OrderItemType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/update_remark`,
    method: "POST",
    data,
  })
}

/** 挂单 */
export function hangOrder(data: Order.CloseOrderType & SensitiveApprovalRequestConfig) {
  const {
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
    ...requestData
  } = data
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order/hang_order`,
    method: "POST",
    data: {
      ...requestData,
      discountList: normalizeOrderDiscountRatesForBackend(requestData.discountList),
    },
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
  })
}

/** 创建商品订单 */
export function createItemOrder(data: Product.ItemOrderVOType[]) {
  const fingerprint = buildOrderRequestFingerprint("standalone", data)
  const requestId = getOrCreateOrderRequestId(fingerprint)
  return request<IApiResponseData<number>>({
    url: `${PREFIX}merchant_oms_order/create_order`,
    method: "POST",
    data,
    customHeaders: {
      "Idempotency-Key": requestId,
    },
  }).then((response) => {
    if (response.code === 0) clearOrderRequestId(fingerprint)
    return response
  })
}

/** 支付宝扫码支付 */
export function aliScanPay(authCode: string, totalAmount: number, orderId: number) {
  return request<IApiResponseData<Order.OrderPayRecordType>>({
    url: `${PREFIX}merchant_oms_order/ali_scan_pay`,
    method: "POST",
    params: {
      orderId,
      authCode,
      totalAmount,
    },
  })
}

/** 微信扫码支付 */
export function wxScanPay(authCode: string, totalAmount: number, orderId: number) {
  return request<IApiResponseData<Order.OrderPayRecordType>>({
    url: `${PREFIX}merchant_oms_order/wx_scan_pay`,
    method: "POST",
    params: {
      orderId,
      authCode,
      totalAmount,
    },
  })
}

/** 支付宝扫码支付 */
export function aliScanPayWithoutOrder(authCode: string, totalAmount: number, shopId: number) {
  return request<IApiResponseData<Order.OrderPayRecordType>>({
    url: `${PREFIX}merchant_oms_order_pay_record/ali_scan_pay`,
    method: "POST",
    params: {
      authCode,
      totalAmount,
      shopId,
    },
  })
}

/** 微信扫码支付 */
export function wxScanPayWithoutOrder(authCode: string, totalAmount: number, shopId: number) {
  return request<IApiResponseData<Order.OrderPayRecordType>>({
    url: `${PREFIX}merchant_oms_order_pay_record/wx_scan_pay`,
    method: "POST",
    params: {
      authCode,
      totalAmount,
      shopId,
    },
  })
}

/**
 * 取消支付订单
 */
export function cancelPayOrder(orderPayRecordId: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_order_pay_record/cancel_pay/${orderPayRecordId}`,
    method: "POST",
  })
}

/** 结账关单 */
export function closeOrder(data: Order.CloseOrderType & SensitiveApprovalRequestConfig) {
  const {
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
    ...requestData
  } = data
  return request<IApiResponseData<Order.OrderPayRecordType>>({
    url: `${PREFIX}merchant_oms_order/close_order`,
    method: "POST",
    data: {
      ...requestData,
      discountList: normalizeOrderDiscountRatesForBackend(requestData.discountList),
    },
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
  })
}

/** 订单销账 */
export function writeOffOrder(data: Order.CloseOrderType & SensitiveApprovalRequestConfig, shiftId?: number) {
  const {
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
    ...requestData
  } = data
  return request<IApiResponseData<boolean>>({
    url: orderMutationUrl("write_off", shiftId),
    method: "POST",
    data: {
      ...requestData,
      discountList: normalizeOrderDiscountRatesForBackend(requestData.discountList),
    },
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
  })
}

/** 查询订单支付状态 */
export function queryPayStatus(orderId: number) {
  return request<IApiResponseData<Order.OrderPayRecordType[]>>({
    url: `${PREFIX}merchant_oms_order/query_pay_status/${orderId}`,
    method: "GET",
  })
}

/** 查询订单支付状态 */
export function queryPayRecordStatus(orderPayRecordId: number) {
  return request<IApiResponseData<Order.OrderPayRecordType>>({
    url: `${PREFIX}merchant_oms_order_pay_record/query_pay_status/${orderPayRecordId}`,
    method: "GET",
  })
}

/** 反结账 */
export function reverseCloseOrder(orderId: number, options?: SensitiveApprovalRequestConfig, shiftId?: number) {
  return request<IApiResponseData<boolean>>({
    url: orderMutationUrl("reverse_close_order", shiftId),
    method: "POST",
    params: {
      orderId,
    },
    sensitiveApprovalAutoSubmit: options?.sensitiveApprovalAutoSubmit,
    sensitiveApprovalBusinessName: options?.sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows: options?.sensitiveApprovalDetailRows,
    sensitiveApprovalReason: options?.sensitiveApprovalReason,
  })
}

/**
 * 获取总订单信息
 * 分页
 *
 * @param query 查询
 * @return {@link R }<{@link TableDataInfo }<{@link OrderPredigestInfo }>
 */
export function getTotalOrderInfo(query: Order.OrderQueryDTO) {
  return request<IApiPageResponse<Order.OrderPredigestInfo>>({
    url: `${PREFIX}merchant_oms_order/total`,
    method: "GET",
    params: query,
  })
}

/**
 * 获取总订单信息数据
 *
 * @param query 查询
 * @return {@link R }<{@link OrderPredigestInfoTuple }>
 */
export function getTotalOrderInfoData(query: Order.OrderQueryDTO) {
  return request<IApiResponseData<Order.OrderPredigestInfoTuple>>({
    url: `${PREFIX}merchant_oms_order/info`,
    method: "GET",
    params: query,
  })
}

/**
 * 获取支付方式列表
 *
 * @return 支付方式列表
 */
export function getShopBussinessInfo() {
  return request<IApiResponseData<Order.MerchantShopPaymentTypeVO[]>>({
    url: `${PREFIX}merchant_shop_payment_type/list`,
    method: "GET",
  })
}

/**
 * 新建支付方式
 *
 * @param data 支付方式信息
 * @return 是否新增成功
 */
export function createShopBussinessInfo(data: Order.MerchantShopPaymentTypeReqDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop_payment_type`,
    method: "POST",
    data,
  })
}

/**
 * 修改支付方式
 *
 * @param data 支付方式信息
 * @returns 是否修改成功
 */
export function updateShopBussinessInfo(data: Order.MerchantShopPaymentTypeReqDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop_payment_type`,
    method: "PUT",
    data,
  })
}

/**
 * 移动支付类型
 * @param data 元组
 * @returns 是否移动成功
 */
export function movePaymentType(data: Order.PaymentTypeSortTuple[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop_payment_type/move`,
    method: "PUT",
    data,
  })
}

/**
 * 获取付款排序信息
 * @returns 付款排序信息
 */
export function getPaymentTypeSorting() {
  return request<IApiResponseData<Order.PaymentTypeSortTuple[]>>({
    url: `${PREFIX}merchant_shop_payment_type/sorting`,
    method: "GET",
  })
}

/**
 * 获取会员付款类型配置
 */
export function getMemberPaymentTypeConfig() {
  return request<IApiResponseData<Order.PaymentTypeExtraConfig>>({
    url: `${PREFIX}merchant_shop_payment_type/member/config`,
    method: "GET",
  })
}

/**
 * 编辑会员付款类型配置
 *
 * @param data 会员付款类型配置信息
 * @returns 是否编辑成功
 */
export function editMemberPaymentTypeConfig(data: Order.MemberPrePaymentInfoDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop_payment_type/member/config`,
    method: "POST",
    data,
  })
}

/**
 * 删除支付方式
 *
 * @param id 支付方式ID
 * @return 是否删除成功
 */
export function deleteShopBussinessInfo(id: string) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_shop_payment_type/${id}`,
    method: "DELETE",
  })
}

// 获取可以被合并的桌台列表
export function getCanBeMerged() {
  return request<IApiResponseData<Order.CanBeMergedTableType>>({
    url: `${PREFIX}merchant/table/can_be_merged`,
    method: "GET",
  })
}

// 获取未关台列表
export function getNotCloseTableList() {
  return request<IApiResponseData<Order.MerchantTableInfoVO[]>>({
    url: `${PREFIX}merchant/table/not_close`,
    method: "GET",
  })
}

/** @deprecated 收银台批量合并请使用 previewMergeTableBatch + confirmMergeTableBatch。 */
export function mergeTable(data: Order.MergeTableRecordRequest) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/merge`,
    method: "POST",
    data,
  })
}

/** 批量合并桌台预检：不写入合并关系，但可能补齐已到期计费段。 */
export function previewMergeTableBatch(data: Order.MergeTableBatchPreviewRequest) {
  return request<IApiResponseData<Order.MergeTableBatchPreviewResponse>>({
    url: `${PREFIX}merchant/table/merge/preview`,
    method: "POST",
    data,
    skipGlobalErrorToast: true,
  })
}

/** 使用一次性预检令牌原子确认批量合并。 */
export function confirmMergeTableBatch(data: Order.MergeTableBatchConfirmRequest) {
  return request<IApiResponseData<Order.MergeTableBatchConfirmResponse>>({
    url: `${PREFIX}merchant/table/merge/confirm`,
    method: "POST",
    data,
    skipGlobalErrorToast: true,
  })
}

// 取消合并
export function cancelMergeTable(data: Order.MergeTableRecordRequest) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/cancel-merge`,
    method: "POST",
    data,
    skipGlobalErrorToast: true,
  })
}

// 桌台预结账
export function tablePreCheckout(data: Order.TablePreCheckoutRequest & SensitiveApprovalRequestConfig) {
  const {
    sensitiveApprovalAutoSubmit,
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
    ...requestData
  } = data
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/pre-checkout`,
    method: "post",
    data: requestData,
    sensitiveApprovalAutoSubmit,
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
  })
}

// 取消桌台预结账
export function cancelTablePreCheckout(data: number) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/cancel-pre-checkout`,
    method: "post",
    data,
  })
}

/** @deprecated 请使用拼写正确的 cancelTablePreCheckout。 */
export const cancleTablePreCheckout = cancelTablePreCheckout
